import ProcessTableDetailsPanel from "./DetailsPanel/ProcessTableDetailsPanel";
import { BulkActionsOptions } from "./Header/BulkActionsOptions";
import { ProcessTableHeader } from "./Header/ProcessTableHeader";
import { ProcessTable } from "./ProcessTable";
import { filterHasError } from "./ProcessTableFilters/utils";
import { TableRow, getProcessTableColumns } from "./ProcessTableUtils";
import ProcessTableQuickFiltersSection from "./QuickFilters/ProcessTableQuickFilters";
import SignalAnalyticsSection from "./SignalAnalytics/SignalAnalyticsSection";
import { ArchivedFilter, ProcessFilterZod, ProcessTableColumnZod, SQLGeneratedProcess } from "@avenueops/shared-types";
import { Drawer, DrawerBody } from "@avenueops/web-components";
import { ProcessView } from "@prisma/client";
import { ColumnDef, ColumnOrderState, ColumnSizingState, VisibilityState, getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { useSelectedKeys } from "context/BulkActionsContext";
import { useProcessTableRows, useProcessTableState, useProcessesForTable } from "context/ProcessTableContext";
import { useUser } from "context/UserContext";
import React, { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import { trpc } from "utils/trpc";

interface Props {
  signalView?: ProcessView;
  archived: ArchivedFilter;
  isDashboard: boolean;
}

const HEADER_HEIGHT = "70px";
const BULK_ACTIONS_HEADER_HEIGHT = "50px";

/**
 * A component for the whole process table page, including the header, list and side panel
 */
export function ProcessTablePage({ signalView, archived, isDashboard }: Props): JSX.Element {
  const [isOpenQuickFilters, setIsOpenQuickFilters] = useState<boolean>(false);
  const user = useUser();

  // get all of the table state data
  const [{ filters, groupBy, search, timeFrame, timeFrameStartIncl, timeFrameEndExcl, columns }, setProcessTableState] = useProcessTableState();
  const refinedFilters = filters?.filter((filter: ProcessFilterZod) => !filterHasError(filter));
  const [debouncedSearch] = useDebounce(search, 500);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [selectedProcess, setSelectedProcess] = useState<SQLGeneratedProcess | null>(null);
  const selectedProcessIds = useSelectedKeys();
  const hasSelected = selectedProcessIds.length > 0;

  // fetch the table data, then flatten it
  const { isFetching, fetchNextPage, groupHeadersAndCount, isLoading } = useProcessesForTable({ archived, isDashboard });
  const processedData: TableRow[] = useProcessTableRows({ archived, isDashboard });

  // set up table
  const defaultColumn: Partial<ColumnDef<TableRow>> = {};

  // these are the actual table columns
  // includes ALL of the meta columns + dynamic columns as set in the process table state
  const tableColumns: ColumnDef<TableRow, any>[] = React.useMemo(
    () =>
      getProcessTableColumns({
        columns,
      }),
    [columns]
  );

  // hide the visibility of the metadata columns that the user has toggled off
  var initialVisibility = {} as VisibilityState;
  for (const column of columns) {
    const columnId = column.id;
    if (columnId) {
      initialVisibility[columnId] = column.visibility;
    }
  }
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>(initialVisibility);

  // set the column order
  const [columnOrder, setColumnOrder] = React.useState<ColumnOrderState>(
    columns.map((col: ProcessTableColumnZod) => {
      return col.id;
    })
  );

  useEffect(() => {
    setColumnOrder(
      columns.map((col: ProcessTableColumnZod) => {
        return col.id;
      })
    );
  }, [columns]);

  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>({});

  // create the table
  const table = useReactTable({
    data: processedData,
    columns: tableColumns,
    defaultColumn,
    state: {
      columnVisibility,
      columnOrder,
      columnSizing,
    },
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    columnResizeMode: "onChange",
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });

  // fetches the total number of processes
  const { data: countData } = trpc.processes.countByGroup.useQuery({
    filters: refinedFilters ?? [],
    groupBy: undefined,
    timeFrame,
    search: debouncedSearch ?? undefined,
    timeFrameStartIncl: timeFrameStartIncl?.toString(),
    timeFrameEndExcl: timeFrameEndExcl?.toString(),
    timeZone,
    archived: archived,
    isDashboard,
  });
  const totalDBRowCount = (countData ? countData[0].count : 0) + (groupHeadersAndCount ? groupHeadersAndCount.length : 0);
  const totalFetched = processedData.length;

  // called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        //once the user has scrolled within 300px of the bottom of the table, fetch more data if there is any
        if (scrollHeight - scrollTop - clientHeight < 500 && !isFetching && totalFetched < totalDBRowCount) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount]
  );

  return (
    <div className="w-full" style={{ height: "100vh" }}>
      <div className={"w-full"} style={{ height: HEADER_HEIGHT }}>
        <ProcessTableHeader
          signalView={signalView}
          setShowQuickFilters={setIsOpenQuickFilters}
          showQuickFilters={isOpenQuickFilters}
          table={table}
          archived={archived}
          isDashboard={isDashboard}
          setSelectedProcess={setSelectedProcess}
        />
      </div>

      {hasSelected ? (
        <div className="flex rounded-t bg-gray-50 border-b border-cool-gray-200" style={{ height: BULK_ACTIONS_HEADER_HEIGHT }}>
          <BulkActionsOptions archived={archived} isDashboard={isDashboard} />
        </div>
      ) : null}
      <div className="w-full" style={{ height: `calc(100vh - ${HEADER_HEIGHT} - ${hasSelected ? BULK_ACTIONS_HEADER_HEIGHT : "0px"})` }}>
        <ProcessTable
          table={table}
          fetchMoreOnBottomReached={fetchMoreOnBottomReached}
          selectedProcess={selectedProcess}
          setSelectedProcess={setSelectedProcess}
        />
      </div>

      {selectedProcess ? (
        <ProcessTableDetailsPanel process={selectedProcess} setProcess={setSelectedProcess} />
      ) : isOpenQuickFilters ? (
        <Drawer
          open={isOpenQuickFilters}
          setOpen={(open: boolean) => {
            if (open) {
              setSelectedProcess(null);
              setIsOpenQuickFilters(true);
            } else {
              setIsOpenQuickFilters(false);
            }
          }}
        >
          <DrawerBody className="px-0 py-0">
            <SignalAnalyticsSection archived={archived} isDashboard={isDashboard} />
            <ProcessTableQuickFiltersSection isDashboard={isDashboard} archived={archived} />
          </DrawerBody>
        </Drawer>
      ) : null}
    </div>
  );
}
